/* eslint-disable no-unused-vars */
import React, {
  memo,
  useEffect,
  useMemo,
} from 'react'
import dynamic from 'next/dynamic'
import { FormattedMessage, useIntl } from 'react-intl'
import { useRouter } from 'next/router'
import Grid from '@mui/material/Grid2'
import Box from '@mui/material/Box'
import Page from 'components/layout/Page'
import usePageProps from 'hooks/usePageProps'
import Skeleton from '@mui/material/Skeleton'
import Makes from 'components/shared/Makes'
import MainBanner from 'components/shared/MainBanner'
import TimelineAndVideo from 'components/shared/TimelineAndVideo'
import useMediaQuery from '@mui/material/useMediaQuery'
import Button from '@mui/material/Button'
import imageDesk from 'public/img/subastas/Imagen_TuPonesElPrecio.jpg'
import imageMainSubastas from 'public/img/subastas/main_image_auction.png'
import { useModal } from 'components/shared/Modal'
import AcutionRegister from 'apps/AcutionRegister'
import AcutionLogin from 'apps/AcutionLogin'
import { Divider, Typography } from '@mui/material'
import EventEmitter from 'services/EventEmitter'
import { useSession } from 'next-auth/react'
import { useAuth } from 'hooks/AuthProvider'
import sx from './styles'

const ReviewsAndPressSkeleton = dynamic(() => import('components/shared/GoogleReviewsAndPress/ReviewsAndPressSkeleton'))
const GoogleReviewsAndPress = dynamic(
  () => import('components/shared/GoogleReviewsAndPress'),
  {
    loading: () => (
      <ReviewsAndPressSkeleton
        showPress
        sx={sx.googleReviewsContainer}
      />
    ),
    ssr: false,
  },
)

const RatingComponent = dynamic(
  () => import('components/shared/Rating'),
  {
    loading: () => (
      <Skeleton
        variant="rectangular"
        animation="wave"
        height={160}
        width="100%"
      />
    ),
    ssr: false,
  },
)

const AuctionLanding = () => {
  const router = useRouter()
  const { formatMessage: f } = useIntl()

  const { makesSYC } = usePageProps()
  const { handleOpenModal, handleCloseModal } = useModal()
  const mainBannerData = {
    title: 'Crece tu inventario con ClikAuto Subastas.',
    subtitle: 'Miles de autos validados mecánica y legalmente. Listos para que los vendas a tu precio.',
    mainImage: {
      title: 'Crece tu inventario con ClikAuto Subastas.',
      url: imageMainSubastas,
      link: imageMainSubastas,
      alt: 'Crece tu inventario con ClikAuto Subastas.',
      description: 'Miles de autos validados mecánica y legalmente. Listos para que los vendas a tu precio.',
      caption: 'Miles de autos validados mecánica y legalmente. Listos para que los vendas a tu precio.',
      width: 1046,
      height: 624,
    },
  }
  const timelineData = {
    image: '',
    mobileImage: '',
    title: 'Subastas ClikAuto, lo mejor para crecer tu inventario.',
    videoUrl: '',
    timeline: [
      {
        title: 'Fácil y cerca de ti.',
        description: 'Ofrecemos múltiples subastas diarias. Compras desde tu celular y recoges en nuestras sucursales en EDO MEX y CDMX.',
        urlType: 'internal_url',
        displayModalTabs: false,
        modalType: 'timeline',
      },
      {
        title: 'Autos de calidad.',
        description: 'Inspeccionados en 150 pts y con documentación al día, listos para la venta.',
        urlType: 'internal_url',
        displayModalTabs: false,
        modalType: 'timeline',
      },
      {
        title: 'Participa cuando quieras.',
        description: 'Previo a cada subasta podrás visualizar la ficha técnica de los autos que te interesen, así como su precio de salida.',
        urlType: 'internal_url',
        displayModalTabs: false,
        modalType: 'timeline',
      },
      {
        title: 'Transparencia total.',
        description: 'Mostramos con lujo de detalle la condición mecánica, estética y legal.',
        urlType: 'internal_url',
        displayModalTabs: false,
        modalType: 'timeline',
      },
      {
        title: '$0 costo.',
        description: 'Sólo pagas una comisión de $1,500 mxn (+IVA) por cada auto comprado.',
        urlType: 'internal_url',
        displayModalTabs: false,
        modalType: 'timeline',
      },
    ],
  }

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const { data, status } = useSession()
  const { isAdvisor, authChecked } = useAuth()

  const isCheckingAuth = !authChecked || status === 'loading'
  const showAdvisorCta = !isCheckingAuth && isAdvisor
  const showGeneralCta = !isCheckingAuth && !isAdvisor && status === 'authenticated'
  const showGuestCta = !isCheckingAuth && !isAdvisor && status === 'unauthenticated'

  const handleRegisterModal = () => {
    handleOpenModal({
      title: <FormattedMessage id="AUCTIONS_LANDING_REGISTER_MODAL_TITLE" />,
      body: (
        <>
          <Typography variant="subtitle1">
            <FormattedMessage id="AUCTIONS_LANDING_REGISTER_MODAL_SUBTITLE" />
          </Typography>
          <Typography
            variant="body1"
            marginY={1}
          >
            <FormattedMessage id="AUCTIONS_LANDING_REGISTER_MODAL_DESCRIPTION" />
          </Typography>
          <Divider />
          <AcutionRegister />
        </>
      ),
      configProps: {
        showClose: true,
        sxProps: sx.login,
        maxWidth: 'xs',
      },
    })
  }

  const handleLoginModal = () => {
    handleOpenModal({
      title: <FormattedMessage id="AUCTIONS_LANDING_LOGIN_MODAL_TITLE" />,
      body: (
        <>
          <Typography
            variant="subtitle1"
            marginY={2}
          >
            <FormattedMessage id="AUCTIONS_LANDING_LOGIN_MODAL_SUBTITLE" />
          </Typography>
          <Divider />
          <AcutionLogin handleCloseModal={handleCloseModal} />
        </>
      ),
      configProps: {
        showClose: true,
        sxProps: sx.login,
        maxWidth: 'xs',
      },
    })
  }

  const sectionsVariables = useMemo(() => [
    {
      id: 'timeline-section',
      class: sx.timelineContainer,
      component: (
        <TimelineAndVideo
          data={timelineData}
          imageEasy={imageDesk}
          imageEasyMobile={imageDesk}
          variant
        >
          <Grid
            container
            gap={2}
            sx={sx.btnsTimelineContainer}
          >

            {showGuestCta && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    lg: 5,
                  }}
                >
                  <Button
                    className="cta_create_account"
                    fullWidth
                    sx={sx.mainButtonTimeline}
                    variant="special"
                    onClick={handleRegisterModal}
                    aria-label="text"
                  >
                    <FormattedMessage
                      id="AUCTIONS_LANDING_REGISTER_NOW"
                    />
                  </Button>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    lg: 5,
                  }}
                >
                  <Button
                    sx={sx.loginButtonTimeline}
                    className="cta_create_account"
                    fullWidth
                    color="primary"
                    variant="outlined"
                    onClick={handleLoginModal}
                    aria-label="text"
                  >
                    <FormattedMessage
                      id="AUCTIONS_LANDING_LOGIN_NOW"
                    />
                  </Button>
                </Grid>
              </>
            )}

          </Grid>
        </TimelineAndVideo>
      ),
      visible: true,
      position: 4,
    },
    {
      id: 'rating-section',
      class: sx.ratingComponent,
      component: (
        <RatingComponent
          text={f({ id: 'AUCTIONS_LANDING_RATING_TITLE' })}
          textVariant="h3"
          variant
        />
      ),
      visible: true,
      position: 1,
    },
    {
      id: 'makes-section',
      component: (
        <Makes
          makes={makesSYC}
          expanded
          variant="row"
          collapsable={false}
          title={f({ id: 'AUCTIONS_LANDING_MAKES_TITLE' })}
          description={f({ id: 'AUCTIONS_LANDING_MAKES_SUBTITLE' })}
          showButton={false}
          cardFormat
        />
      ),
      visible: true,
      position: 2,
    },

  ], [showGuestCta])

  useEffect(() => {
    EventEmitter.suscribe('open-register', handleRegisterModal)
    EventEmitter.suscribe('open-login', handleLoginModal)
    return () => {
      EventEmitter.unsubscribe('open-register')
      EventEmitter.unsubscribe('open-login')
    }
  }, [])

  useEffect(() => {
    if (router?.query?.openLoginModal === 'true' && status === 'unauthenticated') {
      handleLoginModal()
    }
  }, [router?.query, status])

  return (
    <>
      <Page>
        <Box sx={isMobile ? sx.containerMobile : ''}>
          <MainBanner
            {...mainBannerData}
            sx={sx.mainContainer}
          >
            <Grid sx={sx.btnsContainer}>
              {showAdvisorCta && (
                <Button
                  fullWidth
                  sx={sx.registerButton}
                  color="primary"
                  variant="special"
                  onClick={() => router.push('/subastas')}
                  aria-label="Ver registros"
                >
                  Ver registros
                </Button>
              )}

              {showGeneralCta && (
                <Button
                  fullWidth
                  sx={sx.registerButton}
                  color="primary"
                  variant="special"
                  onClick={() => router.push('/subastas/autos')}
                  aria-label="Ver Subastas"
                >
                  Ver Subastas
                </Button>
              )}

              {showGuestCta && (
                <>
                  <Button
                    className="cta_create_account"
                    fullWidth
                    sx={sx.registerButton}
                    color="primary"
                    variant="special"
                    onClick={handleRegisterModal}
                    aria-label="text"
                  >
                    <FormattedMessage
                      id="AUCTIONS_LANDING_REGISTER_NOW"
                    />
                  </Button>
                  <Button
                    sx={sx.loginButton}
                    className="cta_create_account"
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={handleLoginModal}
                    aria-label="text"
                  >
                    <FormattedMessage
                      id="AUCTIONS_LANDING_LOGIN_NOW"
                    />
                  </Button>
                </>
              )}

            </Grid>
          </MainBanner>

          <Box>
            <Grid
              size={12}
              container
              justifyContent="center"
            >
              {sectionsVariables
                .filter((i) => i.visible)
                .sort((a, b) => a.position - b.position)
                .map((section) => (
                  <Grid
                    size={12}
                    sx={section.class ? section.class : null}
                    key={section.id}
                  >
                    {section?.component}
                  </Grid>
                ))}
            </Grid>
          </Box>

          <GoogleReviewsAndPress
            variant="row"
            title="SYC_REVIEWS_TITLE_VARIANT_1_EASY"
            buttonLabel="SYC_SEE_ALL_REVIEWS_1"
            sx={sx.googleReviewsContainer}
          />

        </Box>
      </Page>
    </>
  )
}
export default memo(AuctionLanding)
